import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DepartmentsProvider } from "@/contexts/DepartmentsContext";
import { StockTypesProvider } from "@/contexts/StockTypesContext";
import { MaterialsProvider } from "@/contexts/MaterialsContext";
import { ColoursProvider } from "@/contexts/ColoursContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import StockOut from "@/pages/StockOut";
import Catalog from "@/pages/Catalog";
import Assets from "@/pages/Assets";
import Machinery from "@/pages/Machinery";
import Receive from "@/pages/Receive";
import History from "@/pages/History";
import WorkstationPrint from "@/pages/WorkstationPrint";
import StaffGuidePrint from "@/pages/StaffGuidePrint";
import Users from "@/pages/Users";
import Suppliers from "@/pages/Suppliers";
import Reports from "@/pages/Reports";
import Orders from "@/pages/Orders";
import Labels from "@/pages/Labels";
import BugReports from "@/pages/BugReports";
import StocktakePrint from "@/pages/StocktakePrint";
import StocktakePost from "@/pages/StocktakePost";
import StocktakeAddItemPrint from "@/pages/StocktakeAddItemPrint";
import MachineryAddPrint from "@/pages/MachineryAddPrint";
import AssetAddPrint from "@/pages/AssetAddPrint";
import ITStationAddPrint from "@/pages/ITStationAddPrint";
import SupplierAddPrint from "@/pages/SupplierAddPrint";
import WalkaroundPrint from "@/pages/WalkaroundPrint";
import WalkaroundImport from "@/pages/WalkaroundImport";
import Technicians from "@/pages/Technicians";
import SiteServices from "@/pages/SiteServices";
import ServiceAgreements from "@/pages/ServiceAgreements";
import QuickPhotos from "@/pages/QuickPhotos";
import QuickCosts from "@/pages/QuickCosts";
import SnapImport from "@/pages/SnapImport";

const Protected = ({ children, managerOnly = false }) => {
  const { user, loading, isManager } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-display text-2xl">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (managerOnly && !isManager) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

/** Auth-gated but NOT wrapped in Layout — used for print-friendly routes
 *  that need the full page canvas without the sidebar / topbar. */
const PrintProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-display text-2xl">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <DepartmentsProvider>
      <StockTypesProvider>
      <MaterialsProvider>
      <ColoursProvider>
      <BrowserRouter>
        <Toaster
          position="top-center"
          richColors
          toastOptions={{
            style: {
              border: "3px solid #001740",
              boxShadow: "5px 5px 0 #001740",
              borderRadius: "12px",
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/take" element={<Protected><StockOut /></Protected>} />
          <Route path="/receive" element={<Protected><Receive /></Protected>} />
          <Route path="/catalog" element={<Protected managerOnly><Catalog /></Protected>} />
          <Route path="/assets" element={<Protected><Assets /></Protected>} />
          <Route path="/machinery" element={<Protected><Machinery /></Protected>} />
          <Route path="/technicians" element={<Protected><Technicians /></Protected>} />
          <Route path="/service-agreements" element={<Protected><ServiceAgreements /></Protected>} />
          <Route path="/site-services" element={<Protected><SiteServices /></Protected>} />
          <Route path="/history" element={<Protected><History /></Protected>} />
          <Route path="/suppliers" element={<Protected managerOnly><Suppliers /></Protected>} />
          <Route path="/orders" element={<Protected managerOnly><Orders /></Protected>} />
          <Route path="/reports" element={<Protected managerOnly><Reports /></Protected>} />
          <Route path="/labels" element={<Protected managerOnly><Labels /></Protected>} />
          <Route path="/bug-reports" element={<Protected managerOnly><BugReports /></Protected>} />
          <Route path="/stocktake" element={<Protected managerOnly><StocktakePost /></Protected>} />
          <Route path="/quick-photos" element={<Protected managerOnly><QuickPhotos /></Protected>} />
          <Route path="/quick-costs" element={<Protected managerOnly><QuickCosts /></Protected>} />
          <Route path="/snap-import" element={<Protected managerOnly><SnapImport /></Protected>} />
          <Route path="/stocktake-print" element={<PrintProtected><StocktakePrint /></PrintProtected>} />
          <Route path="/stocktake-add-item-print" element={<PrintProtected><StocktakeAddItemPrint /></PrintProtected>} />
          <Route path="/machinery-add-print" element={<PrintProtected><MachineryAddPrint /></PrintProtected>} />
          <Route path="/assets-add-print" element={<PrintProtected><AssetAddPrint /></PrintProtected>} />
          <Route path="/it-station-add-print" element={<PrintProtected><ITStationAddPrint /></PrintProtected>} />
          <Route path="/supplier-add-print" element={<PrintProtected><SupplierAddPrint /></PrintProtected>} />
          <Route path="/walkaround-print" element={<PrintProtected><WalkaroundPrint /></PrintProtected>} />
          <Route path="/walkaround-import" element={<Protected managerOnly><WalkaroundImport /></Protected>} />
          <Route path="/workstation-print" element={<Protected managerOnly><WorkstationPrint /></Protected>} />
          <Route path="/staff-guide" element={<StaffGuidePrint />} />
          <Route path="/team" element={<Protected managerOnly><Users /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ColoursProvider>
      </MaterialsProvider>
      </StockTypesProvider>
      </DepartmentsProvider>
    </AuthProvider>
  );
}

export default App;
